<section class="migration-monitor">
  <div class="monitor-header">
    <div>
      <h3>Migration Monitor</h3>
      <p>Track workflow migration and application migration task status.</p>
    </div>
    <button type="button" class="inline-link-button" (click)="refresh()">
      REFRESH
    </button>
  </div>

  <div *ngIf="errorMessage" class="monitor-banner error">
    {{ errorMessage }}
  </div>

  <div class="monitor-tabs">
    <button
      type="button"
      class="monitor-tab"
      [class.active]="activeView === 'status'"
      (click)="setView('status')"
    >
      <span>Status</span>
      <small>Workflow and application health</small>
    </button>
    <button
      type="button"
      class="monitor-tab"
      [class.active]="activeView === 'applications'"
      (click)="setView('applications')"
    >
      <span>Applications</span>
      <small>Per-application tasks</small>
    </button>
    <button
      type="button"
      class="monitor-tab"
      [class.active]="activeView === 'advanced'"
      (click)="setView('advanced')"
    >
      <span>Advanced</span>
      <small>Queue, worker, and cache detail</small>
    </button>
  </div>

  <div *ngIf="loading" class="monitor-loading">Loading task status...</div>

  <div *ngIf="activeView === 'status'" class="monitor-section">
    <div class="status-grid">
      <div class="status-card">
        <div class="status-label">Workflow Migration</div>
        <div class="status-value">
          <span
            class="pill"
            [class.processing]="workflowStatus === 'Processing'"
            [class.complete]="workflowStatus === 'Completed'"
            [class.cancelled]="workflowStatus === 'Cancelled'"
            [class.error]="workflowStatus === 'Error'"
            [class.queued]="workflowStatus === 'Queued'"
          >
            {{ workflowStatus }}
          </span>
        </div>
        <div class="status-note">
          {{
            processing?.workflowMigratedOn
              ? 'Workflow migration completed'
              : 'Waiting on workflow migration task'
          }}
        </div>
        <dl *ngIf="latestWorkflowTask" class="task-timing-details">
          <div><dt>Queued On</dt><dd>{{ formatDateText(latestWorkflowTask.queuedOn) }}</dd></div>
          <div><dt>Executed On</dt><dd>{{ formatDateText(latestWorkflowTask.executedOn) }}</dd></div>
          <div><dt>Completed On</dt><dd>{{ formatDateText(latestWorkflowTask.completedOn) }}</dd></div>
          <div><dt>Queued Duration</dt><dd>{{ formatQueuedDuration(latestWorkflowTask) }}</dd></div>
          <div><dt>Execution Duration</dt><dd>{{ formatExecutionDuration(latestWorkflowTask) }}</dd></div>
        </dl>
        <button
          *ngIf="canCancel(latestWorkflowTask)"
          type="button"
          class="cancel-button"
          (click)="cancelTask(latestWorkflowTask!)"
        >
          Cancel Workflow Task
        </button>
      </div>

      <div class="status-card">
        <div class="status-label">Application Migration Coordinator</div>
        <div class="status-value">
          <span
            class="pill"
            [class.processing]="coordinatorStatus === 'Processing'"
            [class.complete]="coordinatorStatus === 'Completed'"
            [class.cancelled]="coordinatorStatus === 'Cancelled'"
            [class.error]="coordinatorStatus === 'Error'"
            [class.queued]="coordinatorStatus === 'Queued' || coordinatorStatus === 'Pending'"
          >
            {{ coordinatorStatus }}
          </span>
        </div>
        <div class="status-note">
          {{ activeCoordinatorTasks.length }} coordinator task(s) queued or executing
        </div>
        <dl *ngIf="latestCoordinatorTask" class="task-timing-details">
          <div><dt>Queued On</dt><dd>{{ formatDateText(latestCoordinatorTask.queuedOn) }}</dd></div>
          <div><dt>Executed On</dt><dd>{{ formatDateText(latestCoordinatorTask.executedOn) }}</dd></div>
          <div><dt>Completed On</dt><dd>{{ formatDateText(latestCoordinatorTask.completedOn) }}</dd></div>
        </dl>
        <button
          *ngIf="activeCoordinatorTasks.length > 0 && canCancel(activeCoordinatorTasks[0])"
          type="button"
          class="cancel-button"
          (click)="cancelTask(activeCoordinatorTasks[0])"
        >
          Cancel Coordinator
        </button>
      </div>

      <div class="status-card">
        <div class="status-label">Applications</div>
        <div class="status-value">
          {{ completedApplications }} / {{ totalApplications || 0 }}
        </div>
        <div class="progress-bar">
          <div
            class="progress-fill"
            [style.width.%]="applicationProgressPercent"
          ></div>
        </div>
        <div class="status-note">
          {{ applicationProgressPercent }}% complete
        </div>
      </div>

      <div class="status-card">
        <div class="status-label">Running</div>
        <div class="status-value">{{ runningApplications }}</div>
        <div class="status-note">Active application migration tasks</div>
      </div>

      <div class="status-card">
        <div class="status-label">Needs Attention</div>
        <div class="status-value danger">{{ failedApplications }}</div>
        <div class="status-note">Failed application tasks</div>
      </div>
    </div>

    <div class="simple-summary">
      <div>
        <strong>Queued:</strong> {{ queuedApplications }} application task(s)
      </div>
      <div>
        <strong>Completed:</strong> {{ completedApplications }} application
        task(s)
      </div>
      <div>
        <strong>Failed:</strong> {{ failedApplications }} application task(s)
      </div>
    </div>

    <div *ngIf="latestError" class="monitor-banner error">
      {{ latestError }}
    </div>

  </div>

  <div *ngIf="activeView === 'applications'" class="monitor-section">
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Application</th>
            <th>Status</th>
            <th>Queued</th>
            <th>Executed</th>
            <th>Completed</th>
            <th>Queued Duration</th>
            <th>Execution Duration</th>
            <th>Result</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of appRows">
            <td>
              <strong>{{ row.applicationIdentifier }}</strong>
              <div class="muted-text">{{ row.id }}</div>
            </td>
            <td>
              <span
                class="pill"
                [class.processing]="row.status === 'Processing'"
                [class.complete]="row.status === 'Completed'"
                [class.cancelled]="row.status === 'Cancelled'"
                [class.error]="row.status === 'Error'"
                [class.queued]="row.status === 'Queued'"
              >
                {{ row.status }}
              </span>
            </td>
            <td>{{ formatDateText(row.queuedOn) }}</td>
            <td>{{ formatDateText(row.executedOn) }}</td>
            <td>{{ formatDateText(row.completedOn) }}</td>
            <td>{{ row.queuedDuration }}</td>
            <td>{{ row.executionDuration }}</td>
            <td>
              <span *ngIf="row.errorMessage" class="error-text">{{
                row.errorMessage
              }}</span>
              <span *ngIf="!row.errorMessage" class="muted-text">{{
                row.status
              }}</span>
            </td>
            <td>
              <button
                *ngIf="canCancel(row.queueItem)"
                type="button"
                class="cancel-button"
                (click)="cancelTask(row.queueItem)"
              >
                Cancel
              </button>
              <span *ngIf="!canCancel(row.queueItem)" class="muted-text"
                >-</span
              >
            </td>
          </tr>
          <tr *ngIf="appRows.length === 0">
            <td colspan="9" class="empty-cell">
              No application migration tasks have been queued.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div *ngIf="activeView === 'advanced'" class="monitor-section advanced-grid">
    <div class="advanced-card">
      <h4>Scheduling Queue</h4>
      <dl>
        <div>
          <dt>Total Queue Items</dt>
          <dd>{{ taskQueueItems.length }}</dd>
        </div>
        <div>
          <dt>Workflow Tasks</dt>
          <dd>{{ workflowTasks.length }}</dd>
        </div>
        <div>
          <dt>Coordinator Tasks</dt>
          <dd>{{ coordinatorTasks.length }}</dd>
        </div>
        <div>
          <dt>Application Tasks</dt>
          <dd>{{ applicationTasks.length }}</dd>
        </div>
        <div>
          <dt>Cancelled Tasks</dt>
          <dd>{{ cancelledTasks }}</dd>
        </div>
      </dl>
    </div>

    <div class="advanced-card">
      <h4>Workers</h4>
      <dl>
        <div>
          <dt>Active Application Tasks</dt>
          <dd>{{ runningApplications }}</dd>
        </div>
        <div>
          <dt>Queued Application Tasks</dt>
          <dd>{{ queuedApplications }}</dd>
        </div>
        <div>
          <dt>Cancelled Application Tasks</dt>
          <dd>{{ cancelledApplications }}</dd>
        </div>
        <div>
          <dt>Failed Application Tasks</dt>
          <dd>{{ failedApplications }}</dd>
        </div>
      </dl>
    </div>

    <div class="advanced-card">
      <h4>Workflow Template Cache</h4>
      <dl>
        <div>
          <dt>Workflow Version</dt>
          <dd>
            {{ processing?.migratedWorkflowId || 'Pending workflow migration' }}
          </dd>
        </div>
        <div>
          <dt>Workspace Pool</dt>
          <dd>{{ workspaceCount }} leased workspace(s)</dd>
        </div>
        <div>
          <dt>Cache Key</dt>
          <dd>WorkflowVersionId</dd>
        </div>
      </dl>
    </div>
  </div>
</section>
